import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Leaf } from "lucide-react";
import { resolveImg } from "@/lib/api";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 + i * 0.12 },
  }),
};

export const KineticHero = ({ content }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const titleWords = content.hero_title.split(" ");
  const mid = Math.ceil(titleWords.length / 2);
  const lines = [titleWords.slice(0, mid).join(" "), titleWords.slice(mid).join(" ")].filter(Boolean);

  return (
    <section id="inicio" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-forest-900">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={resolveImg(content.hero_image)} alt="Bienestar natural Vida Divina" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest-900/70 via-forest-900/40 to-forest-900/90" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center pt-24 pb-16">
        {/* Dynamic logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-4 rounded-full bg-sand/10 backdrop-blur-md border border-sand/20 pl-2 pr-5 py-2">
            <img src={resolveImg(content.logo_url)} alt={content.brand_name} className="h-14 w-14 md:h-16 md:w-16 object-contain rounded-full bg-sand/90 p-1 animate-float" data-testid="hero-logo" />
            <span className="text-sand/90 text-xs md:text-sm tracking-[0.25em] uppercase font-semibold flex items-center gap-2">
              <Leaf size={14} className="text-terracotta-300" /> {content.hero_badge}
            </span>
          </div>
        </motion.div>

        <h1 className="font-serif text-sand text-5xl sm:text-6xl md:text-8xl font-light tracking-hero leading-[0.95] max-w-5xl">
          {lines.map((ln, i) => (
            <span key={i} className="text-mask-line">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {ln}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 max-w-xl text-sand/80 text-base md:text-lg leading-relaxed"
        >
          {content.hero_subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href="#catalogo"
            data-testid="hero-catalog-btn"
            className="group rounded-full bg-terracotta-500 text-white px-8 py-4 text-sm font-semibold hover:bg-terracotta-600 transition-all hover:-translate-y-1 flex items-center gap-2"
          >
            Explorar Catálogo
          </a>
          <a
            href={`https://wa.me/${content.whatsapp_number}?text=${encodeURIComponent(content.whatsapp_message)}`}
            target="_blank" rel="noreferrer"
            data-testid="hero-whatsapp-btn"
            className="rounded-full border border-sand/40 text-sand px-8 py-4 text-sm font-semibold hover:bg-sand hover:text-forest-900 transition-all"
          >
            Hacer Pedido
          </a>
        </motion.div>

        {/* stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-16 grid grid-cols-3 gap-6 max-w-lg border-t border-sand/20 pt-8"
        >
          {[
            { n: content.stat_countries, l: "Países" },
            { n: content.stat_products, l: "Productos" },
            { n: content.stat_guarantee, l: "Garantía" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-serif text-3xl md:text-4xl text-sand">{s.n}</div>
              <div className="text-sand/60 text-xs uppercase tracking-widest mt-1">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-sand/70 flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em]">Desliza</span>
        <ArrowDown size={18} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
